<template>
  <Page :title="pageTitle">
    <template #actions>
      <div class="flex items-center gap-4 text-sm">
        <span v-if="serverError" class="text-red-600" data-cy="cf-error">{{
          serverError
        }}</span>
        <Transition name="cf-fade" mode="out-in">
          <span
            :key="saveStatus"
            class="flex items-center gap-1.5 text-gray-500"
            data-cy="cf-save-status"
          >
            <i
              v-if="saving"
              class="fas fa-circle-notch fa-spin text-gray-400"
            ></i>
            <i
              v-else-if="serverError"
              class="fas fa-circle-exclamation text-red-500"
            ></i>
            <i
              v-else-if="!canSave || dirty"
              class="fas fa-circle-exclamation text-amber-500"
            ></i>
            <i v-else-if="form.id" class="fas fa-check text-green-500"></i>
            {{ saveStatus }}
          </span>
        </Transition>
        <button
          v-if="form.id"
          type="button"
          :disabled="previewing"
          class="flex items-center gap-1.5 font-medium text-blue-600 transition-colors hover:text-blue-800 disabled:opacity-50"
          data-cy="cf-preview-pdf"
          @click="previewPdf"
        >
          <i class="fas fa-file-pdf"></i> Preview PDF
        </button>
        <button
          v-if="!reviewMode"
          type="button"
          class="flex items-center gap-1.5 font-medium text-blue-600 transition-colors hover:text-blue-800"
          data-cy="cf-review"
          @click="reviewMode = true"
        >
          <i class="fas fa-eye"></i> View for Review
        </button>
        <button
          v-else
          type="button"
          class="flex items-center gap-1.5 font-medium text-blue-600 transition-colors hover:text-blue-800"
          data-cy="cf-edit"
          @click="reviewMode = false"
        >
          <i class="fas fa-pen-to-square"></i> Back to editing
        </button>
      </div>
    </template>

    <Modal
      v-if="showPublishPrompt"
      title="Publish this form?"
      class-name="small-modal"
      submit-btn-text="Publish now"
      :saving="publishing"
      @hide-modal="dismissPublishPrompt"
      @submit="publishAndLeave"
    >
      <!-- Modal's root is a teleport, so a data-cy on the component itself is
           dropped instead of reaching the DOM — the hook must sit on real slot
           content or it silently matches nothing in the browser. -->
      <p class="cf-hint" data-cy="cf-publish-prompt">
        This form is saved as a draft, so it can't be attached to jobs yet.
        Publishing makes it available right away.
      </p>
      <!-- The delete slot is the footer's left-hand position, so declining sits
           opposite Publish rather than replacing the whole footer. -->
      <template #delete>
        <button
          type="button"
          class="text-sm font-medium text-gray-600 hover:underline"
          data-cy="cf-publish-later"
          @click="leave"
        >
          Keep as draft
        </button>
      </template>
    </Modal>

    <div v-if="notFound" class="text-gray-600" data-cy="cf-not-found">
      This form could not be found.
    </div>

    <div v-else>
      <template v-if="reviewMode">
        <CustomFormReview :form="form" />
        <div class="flex justify-end pt-6 pb-8">
          <SeraButton text="Done" data-cy="cf-done" @click="done" />
        </div>
      </template>

      <template v-else>
        <div
          class="cf-header mb-4 space-y-4 rounded-lg border border-gray-200 bg-white p-4"
        >
          <InputField
            :model-value="form.name"
            title="Name"
            dense
            placeholder="Form name"
            :validation="nameTouched && !form.name ? 'error' : 'none'"
            :helper-text="nameTouched && !form.name ? 'Name is required' : ''"
            data-cy="cf-name"
            @focusout="nameTouched = true"
            @update:model-value="(v) => updateField('name', v)"
          />
          <TextArea
            :model-value="form.description"
            message="Description"
            placeholder="Optional"
            data-cy="cf-description"
            @update:model-value="(v) => updateField('description', v)"
          />
          <div class="space-y-3 border-t border-gray-200 pt-4">
            <h2
              class="text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Which appointments get this form
            </h2>
            <Toggle
              :checked="form.attach_to_all_appointments"
              label-after="Every new appointment"
              data-cy="cf-attach-all"
              @update:checked="
                (v) => updateField('attach_to_all_appointments', v)
              "
            />
            <!-- The `or` is the point, not decoration: two multiselects side by
                 side is the shape of a filter bar, where terms read as AND. The
                 matcher ORs them, and QA read it the other way round. -->
            <div
              class="grid grid-cols-1 gap-x-3 gap-y-2 md:grid-cols-[1fr_auto_1fr] md:items-end"
            >
              <SelectInput
                :model-value="form.department_ids"
                label="Departments"
                placeholder="None selected"
                :options="departmentOptions"
                multiselect
                filter
                :disabled="form.attach_to_all_appointments"
                data-cy="cf-departments"
                @update:model-value="(v) => updateField('department_ids', v)"
              />
              <!-- Hidden from assistive tech on purpose: a floating "or" between
                   two comboboxes is noise, and the summary below states the same
                   logic as a full sentence, which reads better than a fragment. -->
              <span
                class="pb-3 text-center text-xs text-gray-400"
                aria-hidden="true"
              >
                or
              </span>
              <SelectInput
                :model-value="form.service_category_ids"
                label="Service categories"
                placeholder="None selected"
                :options="serviceCategoryOptions"
                multiselect
                filter
                :disabled="form.attach_to_all_appointments"
                data-cy="cf-service-categories"
                @update:model-value="
                  (v) => updateField('service_category_ids', v)
                "
              />
            </div>
            <!-- Reads the configured rule back in plain language. This is the
                 only place the both-lists-selected case is legible without
                 someone reasoning out the boolean, and it carries the ad-hoc
                 case too, which the placeholders used to state in our words. -->
            <!-- Deliberately unfilled: a rounded grey panel sits directly under
                 two greyed-out inputs and reads as a third disabled control. A
                 left rule marks it as a statement about them instead. -->
            <p
              class="border-l-2 border-gray-300 pl-3 text-sm text-gray-600"
              data-cy="cf-attach-summary"
            >
              {{ attachmentSummary }}
            </p>
          </div>
          <div class="space-y-3 border-t border-gray-200 pt-4">
            <h2
              class="text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Before the job can close
            </h2>
            <Toggle
              :checked="form.required_for_close"
              label-after="Require this form"
              data-cy="cf-required-close"
              @update:checked="(v) => updateField('required_for_close', v)"
            />
          </div>
        </div>

        <div
          v-if="form.sections.length === 0"
          class="cf-enter mb-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-12 text-center"
          data-cy="cf-empty"
        >
          <i class="fas fa-list-check mb-3 text-3xl text-gray-300"></i>
          <p class="mb-4 text-sm text-gray-500">
            No sections yet — add your first section to start building.
          </p>
          <div class="flex justify-center">
            <SeraButton
              icon="fa fa-plus fa-fw"
              text="Add section"
              data-cy="add-section"
              @click="addSection"
            />
          </div>
        </div>

        <template v-else>
          <div class="mb-2 flex items-center justify-between">
            <h2
              class="text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Sections
            </h2>
            <div class="flex items-center gap-3 text-sm">
              <button
                type="button"
                class="text-blue-600 transition-colors hover:text-blue-800"
                data-cy="cf-expand-all"
                @click="setAllExpanded(true)"
              >
                Expand all
              </button>
              <span class="text-gray-300">·</span>
              <button
                type="button"
                class="text-blue-600 transition-colors hover:text-blue-800"
                data-cy="cf-collapse-all"
                @click="setAllExpanded(false)"
              >
                Collapse all
              </button>
            </div>
          </div>

          <Draggable
            :model-value="form.sections"
            item-key="_key"
            handle=".cf-section-handle"
            :animation="150"
            ghost-class="cf-ghost"
            chosen-class="cf-chosen"
            class="space-y-3"
            data-cy="sections-draggable"
            @update:model-value="onSectionsReorder"
          >
            <template #item="{ element, index }">
              <CustomFormSectionEditor
                :model-value="element"
                :index="index"
                :total="form.sections.length"
                :expand-command="expandCommand"
                :active-question-key="activeQuestionKey"
                @update:model-value="(section) => updateSection(index, section)"
                @activate="setActiveQuestion"
                @toggle="onQuestionToggle"
                @remove="removeSection(index)"
                @duplicate="duplicateSection(index)"
                @move-up="moveSection(index, -1)"
                @move-down="moveSection(index, 1)"
              />
            </template>
          </Draggable>
        </template>

        <div class="flex items-center justify-between pt-8 pb-8">
          <SeraButton
            v-if="form.sections.length"
            icon="fa fa-plus fa-fw"
            text="Add section"
            data-cy="add-section"
            @click="addSection"
          />
          <span v-else></span>
          <SeraButton text="Done" data-cy="cf-done" @click="done" />
        </div>
      </template>
    </div>

    <CustomFormOutline
      v-if="!reviewMode && !notFound && form.sections.length > 1"
      :sections="outlineSections"
      :active-question-key="activeQuestionKey"
      :open-question-keys="openKeyList"
      @jump-section="jumpToSection"
      @jump-question="jumpToQuestion"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import Draggable from "vuedraggable";
import { useStore } from "vuex";
import { updatePath } from "@/lib/url.js";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Page from "@/components/Page.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import {
  customFormEditPath,
  customFormPreviewPdfPath,
  customFormsIndexPath
} from "@/mixins/path_helpers";
import {
  type BuilderSection,
  cloneSection,
  newQuestion,
  newSection,
  typeHasOptions,
  typeHasUnit
} from "./customFormFields";
import CustomFormOutline from "./CustomFormOutline.vue";
import CustomFormReview from "./CustomFormReview.vue";
import CustomFormSectionEditor from "./CustomFormSectionEditor.vue";
import { useOutlineScroll } from "./useOutlineScroll";

const props = defineProps<{ templateId?: number | null }>();

const store = useStore();
const router = useRouter();

interface BuilderForm {
  id: number | null;
  published: boolean;
  name: string;
  description: string;
  required_for_close: boolean;
  attach_to_all_appointments: boolean;
  department_ids: number[];
  service_category_ids: number[];
  sections: BuilderSection[];
}

// The client keys a single save sent, in payload order, so the response can be
// zipped back onto whichever live nodes still carry them.
interface SentKeys {
  sectionKey: string;
  questionKeys: string[];
}

const form = reactive<BuilderForm>({
  id: props.templateId ?? null,
  published: false,
  name: "",
  description: "",
  required_for_close: false,
  attach_to_all_appointments: false,
  department_ids: [],
  service_category_ids: [],
  sections: []
});

const removedSectionIds = ref<number[]>([]);
const publishBlockers = ref<string[] | null>(null);
const showPublishPrompt = ref(false);
const publishing = ref(false);
const saving = ref(false);
const pending = ref(false);
// True from the first edit until the next successful save (covers the debounce
// window, an in-flight save, and edits that can't save yet). Drives the
// beforeunload guard so a hard refresh/close can't silently drop work.
const dirty = ref(false);
const serverError = ref<string | null>(null);
const notFound = ref(false);
const nameTouched = ref(false);
const reviewMode = ref(false);
const previewing = ref(false);
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
// Monotonic edit counter. A completing save compares this against the value it
// captured when it snapshotted its payload, so it only reports "saved" when the
// save still represents the current form — otherwise a mid-flight edit would be
// marked clean and lost on a hard refresh (beforeunload sees nothing dirty).
let editSeq = 0;

// --- Outline wayfinding -----------------------------------------------------
// activeQuestionKey = the row your cursor is in (drives the question rail and the
// outline's "current" markers). openQuestionKeys = every expanded question, so
// the outline can show which rows are open at a glance.
const activeQuestionKey = ref<string | null>(null);
const openQuestionKeys = reactive(new Set<string>());
const openKeyList = computed(() => [...openQuestionKeys]);

function setActiveQuestion(key: string) {
  activeQuestionKey.value = key;
}

function onQuestionToggle(key: string, isOpen: boolean) {
  if (isOpen) openQuestionKeys.add(key);
  else openQuestionKeys.delete(key);
}

// The outline drawer takes a flattened tree; the builder's own nodes are keyed
// on client `_key`s because a new question has no id yet.
const outlineSections = computed(() =>
  form.sections.map((section) => ({
    key: section._key,
    name: section.name,
    questions: section.questions.map((question) => ({
      key: question._key,
      title: question.title
    }))
  }))
);

const { jumpToSection, jumpToQuestion } = useOutlineScroll();

const pageTitle = computed(() => (form.id ? "Edit Form" : "New Form"));
// Drafts autosave in any state so no work is lost — only the form name is needed
// to create the record. A PUBLISHED form must stay complete (the backend rejects
// blanking a live form), so the full name/title gate still applies there.
const canSave = computed(() => {
  if (form.name.trim().length === 0) return false;
  if (!form.published) return true;
  return form.sections.every(
    (section) =>
      section.name.trim().length > 0 &&
      section.questions.every((q) => q.title.trim().length > 0)
  );
});
const saveStatus = computed(() => {
  if (saving.value) return "Saving…";
  if (serverError.value) return "Not saved";
  if (form.name.trim().length === 0) return "Add a name to start saving";
  if (!canSave.value) return "Finish required fields to save";
  if (dirty.value) return "Unsaved changes";
  if (form.id) return "All changes saved";
  return "Not saved yet";
});

const departmentOptions = computed(() =>
  store.state.departments.map((d: { id: number; name: string }) => ({
    label: d.name,
    value: d.id
  }))
);
interface ServiceCategoryOption {
  id: number;
  name: string;
  department_id?: number;
}

const departmentNamesById = computed(
  () =>
    new Map<number, string>(
      store.state.departments.map((d: { id: number; name: string }) => [
        d.id,
        d.name
      ])
    )
);

// Qualified with the owning department because a service category belongs to
// exactly one, and that hierarchy is what makes the OR above intelligible: a
// bare "Drain Cleaning" gives no hint it already narrows to Plumbing, so
// nobody could tell that picking categories alone is how you target a subset
// of one department. Falls back to the bare name if the payload omits the
// department, which it does for every consumer that has not opted in.
const serviceCategoryOptions = computed(() =>
  store.state.serviceCategories.map((sc: ServiceCategoryOption) => {
    const department =
      sc.department_id === undefined
        ? undefined
        : departmentNamesById.value.get(sc.department_id);

    return {
      label: department ? `${sc.name} (${department})` : sc.name,
      value: sc.id
    };
  })
);

function namesFor(ids: number[], source: { id: number; name: string }[]) {
  return ids
    .map((id) => source.find((item) => item.id === id)?.name)
    .filter((name): name is string => Boolean(name));
}

// "A", "A or B", "A, B or C" — the connective matches the `or` between the two
// pickers, so the sentence and the layout tell the same story.
function joinWithOr(names: string[]) {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

const attachmentSummary = computed(() => {
  if (form.attach_to_all_appointments) {
    return "Every new appointment gets this form.";
  }

  const departments = joinWithOr(
    namesFor(form.department_ids, store.state.departments)
  );
  const categories = joinWithOr(
    namesFor(form.service_category_ids, store.state.serviceCategories)
  );

  if (!departments && !categories) {
    return "This form won't attach on its own. You can still add it to a job by hand.";
  }
  if (!categories) {
    return `Every new appointment in ${departments} gets this form.`;
  }
  if (!departments) {
    return `Any appointment for ${categories} gets this form.`;
  }
  // "plus" rather than "or" once both lists are set: the union is what people
  // get wrong, and an additive word states it without asking anyone to parse
  // boolean logic.
  return `Every new appointment in ${departments} gets this form, plus any appointment for ${categories}.`;
});

onMounted(async () => {
  window.addEventListener("beforeunload", warnIfDirty);
  store.dispatch("fetchDepartments");
  store.dispatch("fetchServiceCategories");
  if (props.templateId != null) await load(props.templateId);
  syncBreadcrumb();
});

// Drive the app-header breadcrumb (Forms & Checklists › <name>) explicitly rather than
// leaving the nav's DOM-derived fallback to guess it, matching how JobPage and
// CustomerShow name their crumb.
function syncBreadcrumb() {
  const crumbs = [{ label: "Forms & Checklists", url: customFormsIndexPath() }];
  if (form.name) crumbs.push({ label: form.name });
  store.dispatch("setBreadcrumbs", crumbs);
}

onUnmounted(() => {
  window.removeEventListener("beforeunload", warnIfDirty);
  if (debounceTimer) clearTimeout(debounceTimer);
});

const UNSAVED_EXIT_MESSAGE =
  "Your latest changes aren't saved yet. Finish the required fields, or retry, before leaving.";

// SPA route changes don't fire beforeunload, and onUnmounted cancels the debounce
// without saving — so flush any pending edit before leaving. If the flush can't
// land the work, say so and let the author decide: navigating on would unmount
// the error banner along with the unsaved edits. This is the same choice
// beforeunload offers on a hard navigation; done() refuses outright instead,
// because "Done" asks for a save rather than for a way out.
onBeforeRouteLeave(async () => {
  cancelPendingDebounce();
  if (!dirty.value) return true;
  await flushPendingSave();
  if (!dirty.value && !serverError.value) return true;
  return window.confirm(
    serverError.value
      ? `${serverError.value} Leave and lose the changes?`
      : "This form has changes that can't be saved yet. Leave and lose them?"
  );
});

async function load(id: number) {
  try {
    const template = await store.dispatch("fetchCustomFormTemplate", id);
    hydrate(template);
  } catch (error: any) {
    // Only a genuine 404 means "no such form". Reporting that for a 403, a 500,
    // an offline fetch or a hydrate bug sends the author hunting for a form that
    // is sitting right there.
    if (error?.response?.status === 404) {
      notFound.value = true;
      return;
    }
    serverError.value = extractError(error);
    window.Sentry?.captureException(error);
  }
}

// Absent means unknown, not clear — an omitted field must not read as "ready to
// publish", or a stale payload would offer a publish that then fails.
function applyPublishBlockers(json: any) {
  publishBlockers.value = Array.isArray(json?.publish_blockers)
    ? json.publish_blockers
    : null;
}

function hydrate(json: any) {
  form.id = json.id;
  form.published = !!json.published_at;
  applyPublishBlockers(json);
  form.name = json.name ?? "";
  form.description = json.description ?? "";
  form.required_for_close = !!json.required_for_close;
  form.attach_to_all_appointments = !!json.attach_to_all_appointments;
  form.department_ids = json.department_ids ?? [];
  form.service_category_ids = json.service_category_ids ?? [];
  form.sections = (json.sections ?? []).map((section: any) => ({
    ...newSection(),
    id: section.id,
    name: section.name ?? "",
    description: section.description ?? "",
    questions: (section.questions ?? []).map((q: any) => ({
      ...newQuestion(),
      _key: `q-server-${q.id}`,
      id: q.id,
      title: q.title ?? "",
      description: q.description ?? "",
      question_type: q.question_type,
      unit: q.unit,
      required: q.required,
      photo_requirement: q.photo_requirement,
      photo_min_count: q.photo_min_count ?? 0,
      customer_visible: q.customer_visible,
      options: (q.options ?? []).map((o: any) => ({
        token: o.token,
        label: o.label
      }))
    }))
  }));
}

function updateField(key: keyof BuilderForm, value: unknown) {
  (form[key] as unknown) = value;
  scheduleSave();
}

function updateSection(index: number, section: BuilderSection) {
  form.sections[index] = section;
  scheduleSave();
}

function addSection() {
  form.sections.push(newSection());
  scheduleSave();
}

function removeSection(index: number) {
  const [removed] = form.sections.splice(index, 1);
  if (removed?.id) removedSectionIds.value.push(removed.id);
  scheduleSave();
}

function onSectionsReorder(sections: BuilderSection[]) {
  form.sections = sections;
  scheduleSave();
}

function duplicateSection(index: number) {
  const sections = [...form.sections];
  sections.splice(index + 1, 0, cloneSection(sections[index]));
  form.sections = sections;
  scheduleSave();
}

function moveSection(index: number, delta: number) {
  const target = index + delta;
  if (target < 0 || target >= form.sections.length) return;
  const sections = [...form.sections];
  [sections[index], sections[target]] = [sections[target], sections[index]];
  form.sections = sections;
  scheduleSave();
}

// Broadcast an expand/collapse-all to every section. The nonce forces the
// sections' watchers to fire even when the target value repeats.
const expandCommand = ref<{ expand: boolean; nonce: number }>({
  expand: true,
  nonce: 0
});
function setAllExpanded(expand: boolean) {
  expandCommand.value = { expand, nonce: expandCommand.value.nonce + 1 };
}

function scheduleSave() {
  editSeq += 1;
  dirty.value = true;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(save, 800);
}

// Native "unsaved changes" prompt for a hard refresh/close/navigate-away — the
// one path autosave can't cover. SPA route changes don't fire beforeunload, and
// done() flushes first, so this only guards the raw-reload window.
function warnIfDirty(event: BeforeUnloadEvent) {
  if (!dirty.value) return;
  event.preventDefault();
  event.returnValue = "You have unsaved changes.";
}

async function save() {
  if (!canSave.value) return;
  if (saving.value) {
    pending.value = true;
    return;
  }
  saving.value = true;
  serverError.value = null;
  const template = buildPayload();
  const savedSeq = editSeq;
  // Zip the response back onto the form by stable client key, not by object
  // identity: every edit replaces the section object wholesale, so a snapshot
  // ref can be orphaned by the time the response lands.
  const sentKeys = form.sections.map((section) => ({
    sectionKey: section._key,
    questionKeys: section.questions.map((question) => question._key)
  }));
  // Exactly the removals this payload carries. Only these may be cleared on
  // success: a replayed _destroy id is fatal (the row is already gone, so nested
  // attributes raises RecordNotFound → 404, and buildPayload would resend it on
  // every later save), while a removal queued mid-flight was never sent and has
  // to survive into the next payload.
  const sentSectionIds = new Set(removedSectionIds.value);
  const sentQuestionIds = new Map(
    form.sections.map((section) => [
      section._key,
      new Set(section.removedQuestionIds)
    ])
  );
  try {
    const response =
      form.id == null
        ? await create(template)
        : await store.dispatch("updateCustomFormTemplate", {
            id: form.id,
            template
          });
    reconcileIds(response.sections, sentKeys);
    // Every save re-answers whether this form could publish, and the Done prompt
    // reads it — save() applies responses through reconcileIds rather than
    // hydrate, so it has to pick this up itself.
    applyPublishBlockers(response);
    clearSentRemovals(sentSectionIds, sentQuestionIds);
    // Only settle "saved" when nothing changed since the snapshot — otherwise a
    // mid-flight edit would look persisted and be lost on a hard refresh.
    if (editSeq === savedSeq) dirty.value = false;
  } catch (e) {
    serverError.value = extractError(e);
  } finally {
    saving.value = false;
    if (pending.value) {
      pending.value = false;
      await save();
    }
  }
}

async function create(template: unknown) {
  const response = await store.dispatch("createCustomFormTemplate", template);
  form.id = response.id;
  // Reflect the new id in the address bar without a router navigation: swapping
  // routes would remount the builder and re-hydrate (collapsing the section the
  // admin is mid-edit on). This keeps the live instance while the deep link
  // still resolves on refresh (the edit route renders server-side).
  updatePath(customFormEditPath(response.id), true);
  syncBreadcrumb();
  return response;
}

// Done is a confirmation affordance (like the quote flow): edits are already
// autosaved, but clicking Done flushes any pending debounced save and returns
// to the library.
async function done() {
  await flushPendingSave();
  // Stay on the page if the work didn't land, so the reason stays on screen
  // instead of leaving with edits silently unsaved.
  if (serverError.value) return;
  if (dirty.value) {
    serverError.value = UNSAVED_EXIT_MESSAGE;
    return;
  }
  // A draft that would publish cleanly is easy to leave sitting as a draft by
  // accident — nothing on the way out says it is not live yet. Ask before
  // leaving rather than adding another button nobody notices.
  if (canOfferPublish.value) {
    showPublishPrompt.value = true;
    return;
  }
  leave();
}

// Reads the blockers the last save returned, so it reflects the saved form
// rather than unsaved edits — done() has already flushed by this point.
const canOfferPublish = computed(
  () => !form.published && !!form.id && publishBlockers.value?.length === 0
);

function leave() {
  router.push(customFormsIndexPath());
}

// Escape, the backdrop and the header ✕ all land here via Bootstrap's
// hidden.bs.modal. Dismissing cancels the question, not the editing session —
// leaving is only ever an explicit choice, so the author stays on the form.
function dismissPublishPrompt() {
  showPublishPrompt.value = false;
}

async function publishAndLeave() {
  if (!form.id || publishing.value) return;

  publishing.value = true;
  try {
    await store.dispatch("publishCustomFormTemplate", form.id);
    window.notify?.success("Form published");
    leave();
  } catch (error) {
    // Stay put with the reason visible: the form is still a saved draft, so
    // leaving would hide a failure that the author can act on.
    showPublishPrompt.value = false;
    serverError.value = extractError(error);
  } finally {
    publishing.value = false;
  }
}

// The customer-facing PDF, rendered server-side from the last saved state, so
// pending edits are flushed first. The tab opens synchronously in the click
// gesture: Chrome's transient user activation expires during the flush, and a
// window.open after the await risks the popup blocker eating the tab. An
// unsaveable draft still previews — it just shows the last saved state.
async function previewPdf() {
  if (previewing.value) return;
  previewing.value = true;
  const win = window.open("", "_blank");
  try {
    await flushPendingSave();
    const url = customFormPreviewPdfPath(form.id!);
    if (win) {
      win.location.href = url;
    } else {
      window.location.assign(url);
    }
  } finally {
    previewing.value = false;
  }
}

function cancelPendingDebounce() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

// save() returns as soon as it queues a follow-up when a request is already in
// flight, so awaiting it once doesn't mean the newest edit landed. Wait for the
// chain to settle — but capped, so a hung request can't freeze a navigation.
async function flushPendingSave() {
  cancelPendingDebounce();
  await save();
  for (let i = 0; i < 200 && (saving.value || pending.value); i += 1) {
    await new Promise((resolve) => setTimeout(resolve, 25));
  }
}

function buildPayload() {
  const sections_attributes = form.sections.map((section, sIndex) => {
    const questions_attributes: any[] = section.questions.map((q, qIndex) => ({
      ...(q.id ? { id: q.id } : {}),
      title: q.title,
      description: q.description,
      question_type: q.question_type,
      unit: typeHasUnit(q.question_type) ? q.unit : null,
      required: q.required,
      photo_requirement: q.photo_requirement,
      photo_min_count: q.photo_min_count,
      customer_visible: q.customer_visible,
      options: typeHasOptions(q.question_type) ? q.options : [],
      position: qIndex
    }));
    section.removedQuestionIds.forEach((id) =>
      questions_attributes.push({ id, _destroy: true })
    );
    return {
      ...(section.id ? { id: section.id } : {}),
      name: section.name,
      description: section.description,
      position: sIndex,
      questions_attributes
    };
  });
  removedSectionIds.value.forEach((id) =>
    sections_attributes.push({ id, _destroy: true })
  );

  return {
    name: form.name,
    description: form.description,
    required_for_close: form.required_for_close,
    attach_to_all_appointments: form.attach_to_all_appointments,
    department_ids: form.department_ids,
    service_category_ids: form.service_category_ids,
    sections_attributes
  };
}

// Drop the removals the completed save actually carried, leaving anything queued
// while it was in flight for the follow-up payload. Resolving by client key means
// a section replaced by a mid-flight edit still gets its list trimmed.
function clearSentRemovals(
  sentSectionIds: Set<number>,
  sentQuestionIds: Map<string, Set<number>>
) {
  removedSectionIds.value = removedSectionIds.value.filter(
    (id) => !sentSectionIds.has(id)
  );
  form.sections.forEach((section) => {
    const sent = sentQuestionIds.get(section._key);
    if (!sent) return;
    section.removedQuestionIds = section.removedQuestionIds.filter(
      (id) => !sent.has(id)
    );
  });
}

// The server returns the surviving tree in the order we sent it, so we zip
// response nodes to the keys captured at snapshot time and stamp server ids onto
// whichever live node still carries that key. Keying by _key rather than array
// index survives edits, reorders and inserts that land mid-flight — stamping an
// id onto an orphaned object would re-create the row on the next save.
function reconcileIds(responseSections: any[], sentKeys: SentKeys[]) {
  (responseSections ?? []).forEach((respSection, i) => {
    const sent = sentKeys[i];
    if (!sent) return;
    const section = form.sections.find((s) => s._key === sent.sectionKey);
    if (!section) return;
    if (section.id == null) section.id = respSection.id;
    (respSection.questions ?? []).forEach((respQuestion: any, j: number) => {
      const questionKey = sent.questionKeys[j];
      if (!questionKey) return;
      const question = section.questions.find((q) => q._key === questionKey);
      if (question && question.id == null) question.id = respQuestion.id;
    });
  });
}

function extractError(e: any): string {
  const data = e?.response?.data;
  if (data && typeof data === "object") {
    const messages = Object.entries(data).map(
      ([field, msgs]) =>
        `${field} ${([] as string[]).concat(msgs as any).join(", ")}`
    );
    if (messages.length) return messages.join("; ");
  }
  return "Could not save the form.";
}
</script>

<style scoped>
.cf-fade-enter-active,
.cf-fade-leave-active {
  transition: opacity 150ms ease;
}
.cf-fade-enter-from,
.cf-fade-leave-to {
  opacity: 0;
}

.cf-enter {
  animation: cf-enter 200ms ease;
}
@keyframes cf-enter {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Drag feedback for the sections list. */
.cf-ghost {
  opacity: 0.4;
}
.cf-chosen {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

@media (prefers-reduced-motion: reduce) {
  .cf-fade-enter-active,
  .cf-fade-leave-active,
  .cf-enter {
    transition: none;
    animation: none;
  }
}
</style>
